import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { CreditCard } from 'lucide-react'
import { ClientFilterBanner } from './-components/ClientFilterBanner'
import { ChargingQueueTab } from './-components/ChargingQueueTab'
import { CheckoutsTab } from './-components/CheckoutsTab'
import { PaidCustomersTab } from './-components/PaidCustomersTab'
import { RenewalsTab } from './-components/RenewalsTab'
import { PlannerBookTab } from './-components/PlannerBookTab'
import { ChurnedTab } from './-components/ChurnedTab'
import { useStripeIdLookup } from '@/hooks/repositories/useBillingQueueRepository'
import { PageTitle } from '@/components/PageTitle'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

// ─── Route ────────────────────────────────────────────────────────────────────

const billingSearchSchema = z.object({
  clientId: z.string().optional().catch(undefined),
  /**
   * Deep link by Stripe customer (`cus_`) or payment intent (`pi_`) id.
   * Resolved to a client, which then filters BOTH billing tabs; their rows
   * share no Stripe id, only a client, and every row belongs to one.
   *
   * Exists for alerting: a Stripe automation posting to Slack on a failed
   * payment knows one id and nothing else. It carries the customer id when
   * there is one, and the payment intent when there is not — a checkout that
   * fails on its first attempt never creates a customer.
   */
  stripeId: z.string().optional().catch(undefined),
  tab: z
    .enum([
      'queue',
      'checkouts',
      'customers',
      'renewals',
      'planner-book',
      'churned',
    ])
    .optional()
    .catch(undefined),
  scope: z
    .enum([
      'live',
      'processing',
      'completed',
      'expired',
      'failed',
      // Everything but EXPIRED and CANCELED. Added, never substituted: the
      // other six resolve to exactly the statuses they always did, so saved
      // views and deep links written against them are untouched.
      'active',
      'all',
    ])
    .optional()
    .catch(undefined),
  /** Charging Queue lower "view" tab — see BillingFiltersCard's BillingQueueView. */
  view: z
    .enum([
      'active',
      'delayed',
      'awaiting',
      'charged',
      'failed',
      'all',
      'recurring',
    ])
    .optional()
    .catch(undefined),
  /** Charging Queue "Charged" sub-filter — only meaningful when view === 'charged'. */
  chargedSub: z
    .enum(['all', 'hubspot_manual', 'receipt_failed'])
    .optional()
    .catch(undefined),
  /**
   * Checkouts' Deal Stage filter. Comma-separated HubSpot stage ids plus the
   * `unresolved` sentinel — see lib/deal-stage-search-param.
   *
   * Was shared with the Charging Queue (sc-1176) until saved views made the
   * sharing indefensible: applying a Checkouts view wrote this param, so the
   * queue came back filtered by a stage nobody picked while looking at it. The
   * queue owns `qDealStage` now.
   */
  dealStage: z.string().optional().catch(undefined),
  // ─── Checkouts tab filters ──────────────────────────────────────────────
  // Owned by the Checkouts tab specifically, exactly like `scope` above.
  // sc-1170 will add Charging Queue equivalents (its own search/sort/page) —
  // those MUST get their own param names, not reuse these.
  search: z.string().optional().catch(undefined),
  /** Comma-joined `CheckoutExperience` values — same codec as `dealStage`. */
  experience: z.string().optional().catch(undefined),
  /** Comma-joined `CheckoutScheduleFilter` values — same codec as `dealStage`. */
  schedule: z.string().optional().catch(undefined),
  dg: z.enum(['any', 'scheduled', 'not_scheduled']).optional().catch(undefined),
  /**
   * Checkouts: how to treat clients whose sale is declared lost.
   *
   * Absent means `exclude` — the API, not this schema, owns that default, so
   * every link and saved view already in circulation hides them too. Present
   * only to WIDEN the list back out.
   */
  saleLost: z.enum(['exclude', 'include', 'only']).optional().catch(undefined),
  /** Checkouts: has a PRODUCT quote been signed. Confirmations never count. */
  quoteSigned: z
    .enum(['signed', 'unsigned', 'missing'])
    .optional()
    .catch(undefined),
  /** Checkouts' Stripe payment-status filter. The queue has its own qPaymentStatus. */
  paymentStatus: z.string().optional().catch(undefined),
  // Plain string, not an enum: the value space is the 5 sort presets plus 12
  // column sorts (`<column>_asc`/`<column>_desc`), and `.catch(undefined)` on
  // a big enum buys nothing the runtime parse doesn't already give us.
  sort: z.string().optional().catch(undefined),
  /** Id of the applied Checkouts saved view — see useCheckoutsViews. `view` is
   * already taken by the Charging Queue's lower view tab, above. */
  savedView: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  // ─── Charging Queue filters (sc-1170) ───────────────────────────────────
  // Deliberately `q`-prefixed: the un-prefixed names above belong to Checkouts,
  // and both tables live on this one route. No exceptions — `qDealStage` below
  // is the queue's own copy of a filter the two tabs once shared.
  qSearch: z.string().optional().catch(undefined),
  qSource: z.enum(['STRIPE', 'HUBSPOT', 'MANUAL']).optional().catch(undefined),
  // Boolean-as-presence rather than z.coerce.boolean(): coerce turns the
  // STRING "false" into `true`, so a `?qReview=false` link would silently mean
  // the opposite of what it says.
  qReview: z.literal('1').optional().catch(undefined),
  qPastDue: z.literal('1').optional().catch(undefined),
  /** Comma-joined plan family keys plus the `none` sentinel — same codec as `dealStage`. */
  qPlan: z.string().optional().catch(undefined),
  /** Charging Queue's Stripe payment-status filter. Not shared with Checkouts. */
  qPaymentStatus: z.string().optional().catch(undefined),
  /** Charging Queue's Deal Stage filter — same codec as Checkouts' `dealStage`,
   * deliberately its own param so neither tab rewrites the other's. */
  qDealStage: z.string().optional().catch(undefined),
  // Plain string, not an enum, for the same reason `sort` is: the runtime
  // parse in lib/billing-queue-column-sort is the real validator.
  qSort: z.string().optional().catch(undefined),
  qPage: z.coerce.number().int().min(1).optional().catch(undefined),
  // ─── Planner Book filters (sc-1173) ─────────────────────────────────────
  // `pb`-prefixed for the same reason the queue is `q`-prefixed: Checkouts owns
  // the un-prefixed names and all these tables share one route.
  pbSort: z.string().optional().catch(undefined),
  // ─── Renewals filters (sc-1321) ─────────────────────────────────────────
  // `r`-prefixed for the same reason the queue is `q`-prefixed and Planner
  // Book is `pb`-prefixed — Checkouts owns the un-prefixed names and every
  // table on this route needs its own namespace.
  rSearch: z.string().optional().catch(undefined),
  rWindow: z.enum(['30', '60', '90', 'all']).optional().catch(undefined),
  rStartFrom: z.string().optional().catch(undefined),
  rStartTo: z.string().optional().catch(undefined),
  rPaid: z.enum(['paid', 'unpaid']).optional().catch(undefined),
  rExperience: z.string().optional().catch(undefined),
  /** Comma-joined RenewalStatusFilter values — same codec as `dealStage`. */
  rStatus: z.string().optional().catch(undefined),
  /** A single planner's user id. */
  rPlanner: z.string().optional().catch(undefined),
  /** Comma-joined RenewalState values — same codec as `dealStage`. */
  rState: z.string().optional().catch(undefined),
  // Plain string, not an enum — the runtime parse in
  // lib/renewals-column-sort is the real validator, same reasoning as `sort`.
  rSort: z.string().optional().catch(undefined),
  rPage: z.coerce.number().int().min(1).optional().catch(undefined),
})

type BillingSearch = z.infer<typeof billingSearchSchema>

export const Route = createFileRoute('/_app/backoffice/billing/')({
  component: RouteComponent,
  validateSearch: billingSearchSchema,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.billing),
})

// ─── Route Component ──────────────────────────────────────────────────────────

function RouteComponent() {
  const {
    clientId: clientIdParam,
    stripeId,
    tab,
    scope: checkoutsScope,
    view: queueView,
    chargedSub: queueChargedSub,
  } = Route.useSearch()
  const navigate = Route.useNavigate()

  const stripeLookup = useStripeIdLookup(stripeId)

  // A resolved Stripe id acts as a client filter. `clientId` still wins when
  // both are present — it is the explicit one, and the chip that clears it
  // already exists.
  const clientIdFilter =
    clientIdParam ?? stripeLookup.data?.clientId ?? undefined

  // A `?stripeId=` link cannot know which table its row is in — the Stripe
  // alert behind it only knows an id — so the lookup works it out and a bare
  // link opens where the row actually is instead of an empty default tab. An
  // explicit `?tab=` always wins over both.
  //
  // A `cus_…` id is checked BEFORE that lookup, and the order matters: it
  // names a CUSTOMER, and `resolveTarget` can only ever answer 'checkouts' or
  // 'queue' because it was written when the Customers tab could not be
  // filtered to one customer at all. Left after the lookup, the `cus_` case
  // was unreachable for every customer who has a checkout — which is nearly
  // all of them — and those links landed on Checkouts.
  //
  // `pi_…` keeps the old precedence on purpose: a payment intent IS about one
  // payment row, so "where the money is" remains the better answer for it.
  const activeTab =
    tab ??
    (stripeId?.trim().startsWith('cus_')
      ? 'customers'
      : (stripeLookup.data?.target ?? 'checkouts'))
  // Scoping to one customer means wanting everything they have. `live` is the
  // right default for browsing the queue, but it is exactly wrong for a link
  // sent about a FAILED payment: the reversal reopens the intent to OPEN, and a
  // deposit that failed after its final balance was collected stays PROCESSING
  // — so a scope-limited link can land on an empty table for the very row it
  // was sent about.
  const activeCheckoutsScope = checkoutsScope ?? (stripeId ? 'all' : 'live')
  // Same reasoning as the checkouts scope above: a link sent about one payment
  // wants everything that client has, and `active` hides a row the moment it is
  // charged or failed — which is exactly when someone is sent a link about it.
  const activeQueueView = queueView ?? (stripeId ? 'all' : 'active')
  const activeQueueChargedSub = queueChargedSub ?? 'all'
  const stripeIdTrimmed = stripeId?.trim() || undefined
  // The Customers tab doesn't filter by client id at all — it seeds its own
  // search box from the raw Stripe id (see PaidCustomersTab's
  // `stripeIdFilter`), so its banner has to reflect THAT filter truthfully,
  // regardless of whether the id resolved to a client. Without this an
  // orphan `cus_` hits the `notFound` branch below and claims "the tables
  // below are unfiltered", which is false the moment this tab applies it.
  const showCustomersStripeBanner =
    activeTab === 'customers' && Boolean(stripeIdTrimmed)

  const handleTabChange = (value: string) => {
    void navigate({
      // Clear BOTH tables' page params: each tab keeps its own filters, so
      // whichever table was sitting on page 3 would otherwise return to a page
      // its own filters no longer fill.
      search: (prev: BillingSearch) => ({
        ...prev,
        tab: value as BillingSearch['tab'],
        page: undefined,
        qPage: undefined,
        rPage: undefined,
      }),
    })
  }

  const handleClearClientFilter = () => {
    // Clears whichever produced the filter — leaving `stripeId` behind would
    // re-apply it on the next render and the chip would look broken.
    void navigate({
      search: (prev: BillingSearch) => ({
        ...prev,
        clientId: undefined,
        stripeId: undefined,
      }),
    })
  }

  const handleCheckoutsScopeChange = (value: BillingSearch['scope']) => {
    void navigate({
      // Clear page in the same navigate — a narrower scope can have fewer
      // pages than the one being left, which would strand the table on an
      // empty page.
      search: (prev: BillingSearch) => ({
        ...prev,
        scope: value,
        page: undefined,
      }),
    })
  }

  const handleQueueViewChange = (value: BillingSearch['view']) => {
    void navigate({
      search: (prev: BillingSearch) => ({
        ...prev,
        view: value,
        // The charged sub-filter is only meaningful under the Charged view —
        // clear it when navigating away so it never lingers stale in the URL.
        ...(value !== 'charged' ? { chargedSub: undefined } : {}),
      }),
      replace: true,
    })
  }

  const handleQueueChargedSubChange = (value: BillingSearch['chargedSub']) => {
    void navigate({
      search: (prev: BillingSearch) => ({ ...prev, chargedSub: value }),
      replace: true,
    })
  }

  return (
    <div className="py-4 space-y-4">
      <PageTitle title="Billing Management" />

      <div className="flex items-center gap-3">
        <div className="flex size-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <CreditCard className="size-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Billing Management
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Manage billing queue, paid customers, refunds, and subscriptions.
          </p>
        </div>
      </div>

      {/* One banner for both tables — they are filtered by the same thing.
          Customers is checked first: it never filters via `clientIdFilter`,
          so that branch would otherwise win with a banner describing a
          filter this tab isn't actually applying. */}
      {showCustomersStripeBanner ? (
        <ClientFilterBanner
          clientName={stripeLookup.data?.clientName}
          stripeId={stripeIdTrimmed}
          target="customers"
          onClear={handleClearClientFilter}
        />
      ) : clientIdFilter ? (
        <ClientFilterBanner
          clientName={stripeLookup.data?.clientName}
          stripeId={stripeIdTrimmed}
          target={stripeLookup.data?.target ?? null}
          onClear={handleClearClientFilter}
        />
      ) : stripeId && !stripeLookup.isLoading ? (
        <ClientFilterBanner notFound stripeId={stripeId.trim()} />
      ) : null}

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="min-w-0"
      >
        {/* Below md the six tabs (~574px) exceed the viewport, so the list
            scrolls inside itself instead of widening the page. h-12 makes the
            triggers >=44px tall for touch; desktop is unchanged. */}
        <TabsList className="max-md:h-12 max-md:w-full max-md:max-w-full max-md:justify-start max-md:overflow-x-auto max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden max-md:[&>*]:flex-none">
          <TabsTrigger value="checkouts">Checkouts</TabsTrigger>
          <TabsTrigger value="queue">Charging Queue</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="renewals">Renewals</TabsTrigger>
          <TabsTrigger value="planner-book">Planner Book</TabsTrigger>
          <TabsTrigger value="churned">Churned</TabsTrigger>
        </TabsList>

        <TabsContent value="checkouts">
          <CheckoutsTab
            activeScope={activeCheckoutsScope}
            onScopeChange={handleCheckoutsScopeChange}
            clientIdFilter={clientIdFilter}
          />
        </TabsContent>

        <TabsContent value="queue">
          <ChargingQueueTab
            clientIdFilter={clientIdFilter}
            activeView={activeQueueView}
            onViewChange={handleQueueViewChange}
            activeChargedSub={activeQueueChargedSub}
            onChargedSubChange={handleQueueChargedSubChange}
          />
        </TabsContent>

        <TabsContent value="customers">
          <PaidCustomersTab
            stripeIdFilter={stripeIdTrimmed}
            onClearStripeFilter={handleClearClientFilter}
          />
        </TabsContent>

        <TabsContent value="renewals">
          <RenewalsTab />
        </TabsContent>

        <TabsContent value="planner-book">
          <PlannerBookTab />
        </TabsContent>

        <TabsContent value="churned">
          <ChurnedTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
